import React, { ReactElement } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled, { css } from 'styled-components'

import { variables } from 'src/Styled/variables'
import Loader from 'src/Assets/Svg/loader.svg?react'
import { DEBOUNCE_DELAY_100 } from 'src/Constants/constants'

export interface RedButtonProps extends React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> {
  loading?: boolean
}

const StyledButton = styled.button<{
  $loading?: boolean
}>`
  white-space: nowrap;
  position: relative;
  display: flex;
  height: 48px;
  padding: 8px 40px;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
  gap: 8px;
  background: ${({ theme }) => theme.color.lightRed};
  border: 1px solid ${({ theme }) => theme.color.lightRed};
  color: ${({ theme }) => theme.color.darkRed};

  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
  cursor: pointer;

  transition: all 0.2s;

  svg {
    path {
      fill: currentColor;
    }
  }

  &:hover {
    background: ${({ theme }) => theme.color.lightGrey};
    border-color: ${({ theme }) => theme.color.lightGrey};
  }

  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.color.lightRed};
    border-color: ${({ theme }) => theme.color.darkGreen};
    color: ${({ theme }) => theme.color.darkGreen};
  }

  &:active {
    outline: none;
    background: ${({ theme }) => theme.color.hoverWhite};
    border-color: ${({ theme }) => theme.color.lightGrey};
    color: ${({ theme }) => theme.color.mediumGrey};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.color.hoverWhite};
    border-color: ${({ theme }) => theme.color.lightGrey};
    color: ${({ theme }) => theme.color.lightGrey};
  }

  ${({ $loading }) =>
    $loading &&
    css`
      &:disabled {
        cursor: default;
      }
    `}
`

const LoaderWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  svg {
    width: 17px;
    height: 17px;

    path {
      fill: ${({ theme }) => theme.color.mediumGrey};
    }
  }
`

const ChildrenWrapper = styled.div<{ $loading?: boolean }>`
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

export const RedButton: React.FC<RedButtonProps> = (props): ReactElement => {
  const { children, loading, onClick, disabled, ...restProps } = props

  const debouncedOnClick = useDebouncedCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof onClick === 'function') {
        onClick(event)
      }
    },
    DEBOUNCE_DELAY_100,
  )

  return (
    <StyledButton
      {...restProps}
      onClick={(e) => debouncedOnClick(e)}
      disabled={disabled || loading}
      $loading={loading}
    >
      {loading && !disabled && (
        <LoaderWrapper>
          <Loader data-testid='loader' />
        </LoaderWrapper>
      )}

      <ChildrenWrapper $loading={loading}>{children}</ChildrenWrapper>
    </StyledButton>
  )
}
